/**
 * This hook is used to get the window dimensions.
 * 
 * SRC: https://dev.to/adrien/creating-a-custom-react-hook-to-get-the-window-s-dimensions-in-next-js-135k
 * 
 * Adjusted the default values of the state for a faster initial render.
 */

import { DeviceSize } from '@/types'
import { useCallback, useEffect, useState } from 'react'

type WindowDimentions = {
    width: number | undefined
    height: number | undefined
}

const useWindowDimensions = () => {
    const [windowDimensions, setWindowDimensions] = useState<WindowDimentions>(() => ({
        width: typeof window !== 'undefined' ? window.innerWidth : undefined,
        height: typeof window !== 'undefined' ? window.innerHeight : undefined,
    }))
    const [deviceSize, setDeviceSize] = useState<DeviceSize>()

    const handleResize = useCallback((): void => {
        setWindowDimensions({
            width: window.innerWidth,
            height: window.innerHeight,
        })
        if (window.innerWidth < 768) {
            setDeviceSize(DeviceSize.SM)
        } else if (window.innerWidth < 1024) {
            setDeviceSize(DeviceSize.MD)
        } else {
            setDeviceSize(DeviceSize.LG)
        }
    }, [])

    const displayIfSize = useCallback((size: DeviceSize[]): boolean => {
        return size.includes(deviceSize!)
    }, [deviceSize])

    useEffect(() => {
        handleResize()
        window.addEventListener('resize', handleResize)
        return (): void => window.removeEventListener('resize', handleResize)
    }, [handleResize]) // Empty array ensures that effect is only run on mount

    return { ...windowDimensions, deviceSize, displayIfSize }
}

export default useWindowDimensions

